import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { AppNotification, NotificationsService } from 'app';
import { I18nService } from 'i18n';

/**
 * `community/notifications` — the full notification list (the header bell
 * only shows the latest few). Reuses `NotificationsService` from `app`
 * directly rather than a community-local wrapper.
 */
@Component({
  selector: 'app-community-notifications-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslocoModule],
  templateUrl: './community-notifications-page.component.html',
  styleUrl: './community-notifications-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityNotificationsPageComponent implements OnInit {
  private notifications = inject(NotificationsService);
  private i18n = inject(I18nService);
  private router = inject(Router);

  items = signal<AppNotification[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  unreadOnly = signal(false);

  page = signal(1);
  totalPages = signal(1);
  total = signal(0);

  ngOnInit(): void {
    this.load(1);
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  communityLink(): string {
    return `/${this.lang}/community`;
  }

  settingsLink(): string {
    return `/${this.lang}/community/settings`;
  }

  toggleUnreadOnly(): void {
    this.unreadOnly.update(v => !v);
    this.load(1);
  }

  load(page: number): void {
    this.loading.set(true);
    this.error.set(null);
    this.notifications.list({ page, perPage: 20, unreadOnly: this.unreadOnly() }).subscribe({
      next: res => {
        this.items.set(res.data);
        this.page.set(res.page);
        this.totalPages.set(res.total_pages);
        this.total.set(res.total_count);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('community.errors.generic');
      }
    });
  }

  open(n: AppNotification): void {
    if (!n.read) {
      this.notifications.markRead(n.id).subscribe({
        next: () => this.notifications.refresh(),
        error: () => {}
      });
      this.items.update(list => list.map(x => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.url) this.router.navigateByUrl(`/${this.lang}${n.url}`);
  }

  markAllRead(): void {
    this.notifications.markAllRead().subscribe({
      next: () => {
        this.items.update(list => list.map(n => ({ ...n, read: true })));
      },
      error: () => {}
    });
  }
}
